import { useChatStore } from '@/stores/chatStore';
import { sendStreamingMessage } from '@/features/chat/lib/logic';
import { createStreamHandler } from '@/features/chat/lib/streamHandler';

interface UseChatHandlersProps {
    conversationId: string | null;
    currentConversationId: string | null;
    onNewConversation: (conversationId: string) => void;
}

export function useChatHandlers({ conversationId, currentConversationId, onNewConversation }: UseChatHandlersProps) {
    const {
        addMessage,
        isStreaming,
        startStreamingMessage,
        appendToStreamingMessage,
        finishStreamingMessage,
        popLastMessage,
        getLastUserMessage,
        setCurrentConversationId,
        updateMessageId,
    } = useChatStore();

    const handleChatSubmit = async (message: string, isResubmit: boolean = false) => {
        // Add user message
        if (!isResubmit) {
            addMessage({ role: 'user', content: message });
        }

        try {
            // Start streaming message
            const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            startStreamingMessage(messageId);

            const streamHandler = createStreamHandler(messageId, conversationId, currentConversationId, {
                appendToStreamingMessage,
                finishStreamingMessage,
                updateMessageId,
                setCurrentConversationId,
                onStreamingComplete: onNewConversation,
            });

            await sendStreamingMessage(message, streamHandler, currentConversationId || undefined);

            // Ensure streaming is finished even if no 'done' chunk was received
            if (isStreaming) {
                finishStreamingMessage();
            }
        } catch (error) {
            console.error('Chat error:', error);
            if (isStreaming) {
                appendToStreamingMessage(`\n\n[Error: ${error instanceof Error ? error.message : 'Unknown error'}]`);
                finishStreamingMessage();
            }
        }
    };

    const handleResubmitLastUserMessage = () => {
        const lastUserMessage = getLastUserMessage();
        if (lastUserMessage) {
            popLastMessage();
            handleChatSubmit(lastUserMessage.content, true);
        }
    };

    return {
        handleChatSubmit,
        handleResubmitLastUserMessage,
    };
}
